import { useEffect, useMemo, useState } from 'react'
import { useWeb3React } from '@web3-react/core'
import { useSelector, batch } from 'react-redux'
import { useAppDispatch } from 'state'
import { useFastRefreshEffect } from 'hooks/useRefreshEffect'
import { State } from '../types'
import { LotteryTicket, LotteryTicketClaimData } from 'constants/types'
import {
  fetchCurrentLotteryId,
  fetchCurrentLottery,
  fetchUserTicketsAndLotteries,
  fetchPublicLotteries,
  fetchLotteryAutoConfig,
} from '.'
import { makeLotteryGraphDataByIdSelector, lotterySelector } from './selectors'
import { fetchUserLotteries } from 'state/lottery'
import { usePriceMjtUSDT } from 'state/hooks'
import { useLotteryV2Contract } from 'hooks/useContract'
import useToast from 'hooks/useToast'
import { callWithEstimateGas } from 'utils/estimateGas'
import BN from 'bignumber.js'

// Lottery
export const useGetCurrentLotteryId = () => {
  return useSelector((state: State) => state.lottery?.currentLotteryId)
}

export const useGetUserLotteriesGraphData = () => {
  return useSelector((state: State) => state.lottery.userLotteryData)
}

export const useGetLotteriesGraphData = () => {
  return useSelector((state: State) => state.lottery.lotteriesData)
}

export const useGetLotteriesAutoConfigData = () => {
  return useSelector((state: State) => state.lottery.userAutoConfig)
}

export const useGetLotteryGraphDataById = (lotteryId: string) => {
  const lotteryGraphDataByIdSelector = useMemo(() => makeLotteryGraphDataByIdSelector(lotteryId), [lotteryId])
  return useSelector(lotteryGraphDataByIdSelector)
}

export const useFetchLottery = (fetchPublicDataOnly = false) => {
  const { account } = useWeb3React()
  const dispatch = useAppDispatch()
  const currentLotteryId = useGetCurrentLotteryId()
  useEffect(() => {
    // get current lottery ID & max ticket buy
    dispatch(fetchCurrentLotteryId())
  }, [dispatch])

  useFastRefreshEffect(() => {
    if (currentLotteryId) {
      batch(() => {
        // Get historical lottery data from nodes +  last 100 subgraph entries
        dispatch(fetchPublicLotteries({ currentLotteryId }))
        // get public data for current lottery
        dispatch(fetchCurrentLottery({ currentLotteryId }))
      })
    }
  }, [dispatch, currentLotteryId])

  useEffect(() => {
    // get user tickets for current lottery, and user lottery subgraph data
    if (account && currentLotteryId && !fetchPublicDataOnly) {
      dispatch(fetchUserTicketsAndLotteries({ account, currentLotteryId }))
    }
  }, [dispatch, currentLotteryId, account, fetchPublicDataOnly])
}

export const useLottery = () => {
  return useSelector(lotterySelector)
}

export const useFetchAutoConfig = () => {
  const { account } = useWeb3React()
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (account) {
      dispatch(fetchLotteryAutoConfig({ account }))
    }
  }, [account, dispatch])
}

// check mjt price is more than one dollar
export const useMjtPriceMoreThanOne = () => {
  const mjtPrice = usePriceMjtUSDT()
  const isMoreThanOne = useMemo(() => {
    return new BN(mjtPrice).gt(1)
  }, [mjtPrice])
  return isMoreThanOne
}

// claim reward callback
export function useClaimCallback(roundsToClaim: LotteryTicketClaimData[]): {
  claimCallback: () => Promise<void>
  pending: boolean
} {
  const dispatch = useAppDispatch()
  const { account } = useWeb3React()
  const { toastSuccess, toastError } = useToast()
  const [pendingTx, setPendingTx] = useState(false)
  const { maxNumberTicketsPerBuyOrClaim, currentLotteryId } = useLottery()

  const lotteryContract = useLotteryV2Contract()

  const parseUnclaimedTicketDataForClaimCall = (ticketsWithUnclaimedRewards: LotteryTicket[], lotteryId: string) => {
    const ticketIds = ticketsWithUnclaimedRewards?.map((ticket) => {
      return ticket.id
    })
    const brackets = ticketsWithUnclaimedRewards?.map((ticket) => {
      return ticket.rewardBracket
    })
    return { lotteryId, ticketIds, brackets }
  }

  const activeLotteryId = useMemo(() => {
    const idList = []
    for (let i = 0; i < roundsToClaim.length; i++) {
      idList.push(roundsToClaim[i].roundId)
    }
    return idList
  }, [roundsToClaim])

  const activeClaimData = useMemo(() => {
    const data = []
    for (let i = 0; i < roundsToClaim.length; i++) {
      data.push(
        parseUnclaimedTicketDataForClaimCall(roundsToClaim[i]?.ticketsWithUnclaimedRewards, roundsToClaim[i]?.roundId)
      )
    }
    return data
  }, [roundsToClaim])

  // const claimTicketsCallData = parseUnclaimedTicketDataForClaimCall(
  //   activeClaimData?.ticketsWithUnclaimedRewards,
  //   activeClaimData?.roundId
  // )

  // const shouldBatchRequest = maxNumberTicketsPerBuyOrClaim.lt(claimTicketsCallData?.ticketIds?.length)


  const getTicketBatches = (ticketIds: string[], brackets: number[]): { ticketIds: string[]; brackets: number[] }[] => {
    const requests = []
    const maxAsNumber = maxNumberTicketsPerBuyOrClaim?.toNumber()

    for (let i = 0; i < ticketIds.length; i += maxAsNumber) {
      const ticketIdsSlice = ticketIds.slice(i, maxAsNumber + i)
      const bracketsSlice = brackets.slice(i, maxAsNumber + i)
      requests.push({ ticketIds: ticketIdsSlice, brackets: bracketsSlice })
    }

    return requests
  }

  // const handleClaim = async () => {
  //   const { lotteryId, ticketIds, brackets } = claimTicketsCallData
  //   setPendingTx(true)
  //   const receipt = await callWithEstimateGas(lotteryContract, 'claimTickets', [lotteryId, ticketIds, brackets])
  //   const result = await receipt.wait()
  //   if (result?.status) {
  //     toastSuccess(`Prizes Collected!`, `Your MJT prizes for round ${lotteryId} have been sent to your wallet`)
  //     handleProgressToNextClaim()
  //   } else {
  //     toastError('Error', 'Please try again. Confirm the transaction and make sure you are paying enough gas!')
  //   }
  //   setPendingTx(false)
  // }

  const handleBatchClaim = async () => {
    const promises: any[] = []
    setPendingTx(true)
    console.log('----- set pending true -----')
    // eslint-disable-next-line no-restricted-syntax
    for (const ticketData of activeClaimData) {
      const shouldBatchRequest = maxNumberTicketsPerBuyOrClaim.lt(ticketData?.ticketIds?.length)
      if (shouldBatchRequest) {
        const ticketBatches = getTicketBatches(ticketData?.ticketIds, ticketData?.brackets)
        // eslint-disable-next-line no-restricted-syntax
        for (const ticketBatch of ticketBatches) {
          promises.push(
            callWithEstimateGas(lotteryContract, 'claimTickets', [
              ticketData.lotteryId,
              ticketBatch.ticketIds,
              ticketBatch.brackets,
            ])
          )
        }
      } else {
        promises.push(
          callWithEstimateGas(lotteryContract, 'claimTickets', [
            ticketData.lotteryId,
            ticketData.ticketIds,
            ticketData.brackets,
          ])
        )
      }
    }
    await Promise.all(promises)
      .then(async (receipts) => {
        const lastReceipt = receipts[receipts.length - 1]
        const result = await lastReceipt.wait()
        if (result?.status) {
          toastSuccess(
            `Prizes Collected!`,
            `Your MJT prizes for round ${activeLotteryId.toString()} have been sent to your wallet`
          )
          dispatch(fetchUserLotteries({ account, currentLotteryId }))
        } else {
          toastError('Error', 'Please try again. Confirm the transaction and make sure you are paying enough gas!')
        }
      })
      .catch(() => {
        toastError('Error', 'Please try again. Confirm the transaction and make sure you are paying enough gas!')
      })
    console.log('----- set pending false -----')
    setPendingTx(false)
  }

  return {
    claimCallback: handleBatchClaim,
    pending: pendingTx,
  }
}
